import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { HolderType } from '@savgroup-front-common/types';
import { QuoteClientAnswer } from '@savgroup-front-common/types/Quotation/QuoteClientAnswer';

import { CommonRepairerService } from '../../../../api/Repairer';
import { useToasts } from '../../../../molecules/NotificationsProvider';
import { GetQuotationsByFileIdQuery } from '../../hooks/useGetQuotationsByFileIdQuery';

export const useRejectQuotation = ({
  quotationId,
  fileId,
  onAnswerSuccess,
  holderType,
}: {
  quotationId: string;
  fileId: string;
  onAnswerSuccess?: () => void;
  holderType: HolderType;
}) => {
  const queryClient = useQueryClient();
  const [isConfirmRejectModalOpen, setIsConfirmRejectModalOpen] =
    useState(false);
  const { pushErrors } = useToasts();

  const { mutateAsync: handleReject, isLoading: isRejectLoading } = useMutation(
    [],
    async () => {
      const response = await CommonRepairerService.setQuoteClientAnswerCommand({
        quoteId: quotationId,
        clientAnswer: QuoteClientAnswer.REJECTED,
      });

      if (response.failure) {
        pushErrors(response.errors);

        return undefined;
      }

      if (onAnswerSuccess) {
        onAnswerSuccess();
      }

      await queryClient.invalidateQueries({
        queryKey: [GetQuotationsByFileIdQuery, { fileId }],
      });

      setIsConfirmRejectModalOpen(false);

      return undefined;
    },
  );

  const handleRejectButtonClick = useCallback(async () => {
    if (holderType === HolderType.OWNER) {
      await handleReject();

      return;
    }
    setIsConfirmRejectModalOpen(true);
  }, [handleReject, holderType]);
  const handleCancelConfirmReject = useCallback(() => {
    setIsConfirmRejectModalOpen(false);
  }, []);

  const {
    mutateAsync: handleRejectWithDestruction,
    isLoading: isRejectWithDestructionLoading,
  } = useMutation([], async ({ rejectReason }: { rejectReason: string }) => {
    const response = await CommonRepairerService.setQuoteClientAnswerCommand({
      quoteId: quotationId,
      clientAnswer: QuoteClientAnswer.REJECTED_WITH_DESTRUCTION,
      rejectReason,
    });

    if (response.failure) {
      pushErrors(response.errors);

      return undefined;
    }

    if (onAnswerSuccess) {
      onAnswerSuccess();
    }

    await queryClient.invalidateQueries({
      queryKey: [GetQuotationsByFileIdQuery, { fileId }],
    });
    await queryClient.invalidateQueries({
      queryKey: ['getInvoicesQuery', { fileId }],
    });

    setIsConfirmRejectModalOpen(false);

    return undefined;
  });

  const {
    mutateAsync: handleRejectWithReturn,
    isLoading: isRejectWithReturnLoading,
  } = useMutation([], async ({ rejectReason }: { rejectReason: string }) => {
    const response = await CommonRepairerService.setQuoteClientAnswerCommand({
      quoteId: quotationId,
      clientAnswer: QuoteClientAnswer.REJECTED_WITH_RETURN,
      rejectReason,
    });

    if (response.failure) {
      pushErrors(response.errors);

      return undefined;
    }

    if (onAnswerSuccess) {
      onAnswerSuccess();
    }

    await queryClient.invalidateQueries({
      queryKey: [GetQuotationsByFileIdQuery, { fileId }],
    });
    await queryClient.invalidateQueries({
      queryKey: ['getInvoicesQuery', { fileId }],
    });

    setIsConfirmRejectModalOpen(false);

    return undefined;
  });

  return {
    handleRejectButtonClick,
    handleCancelConfirmReject,
    isConfirmRejectModalOpen,
    handleRejectWithReturn,
    handleRejectWithDestruction,
    isRejectLoading:
      isRejectWithReturnLoading ||
      isRejectWithDestructionLoading ||
      isRejectLoading,
  };
};
